// Terrain
// Features:
// - Terrain mesh with standard material
// - Instantiated trees
// - Environment lighting (fog, ambient light)
// - Dynamic ambient dimming

import { useMemo, useRef } from 'react'
import { useGLTF, useTexture } from '@react-three/drei'
import * as THREE from 'three'

import Tree from './Tree'
import Fog from './Fog'
import AmbientDimmer from './AmbientDimmer'

import terrainTextureURL from '/textures/terrain.webp'
import treesData from '../../instanceData/trees.json'

const TERRAIN_CONFIG = {
    ambientIntensity: 0.21,
    fogRadius: 55,
}

export default function Terrain() {
    const ambientRef = useRef()

    // Geometry
    const { nodes } = useGLTF('/models/terrain.glb')

    // Textures
    const terrainTexture = useTexture(terrainTextureURL, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace
        texture.flipY = false
    })

    // Material
    const material = useMemo(
        () =>
            new THREE.MeshStandardMaterial({
                map: terrainTexture,
                side: THREE.DoubleSide,
                roughness: 1.0,
                metalness: 0.0,
            }),
        [terrainTexture]
    )

    return (
        <group>
            <Fog fogRadius={TERRAIN_CONFIG.fogRadius} />
            <ambientLight
                ref={ambientRef}
                intensity={TERRAIN_CONFIG.ambientIntensity}
            />

            {/* Ground / Terrain */}
            <mesh
                geometry={nodes.terrain.geometry}
                material={material}
                position={[2.039, -0.229, 0.014]}
                rotation={[0, -0.611, 0]}
            />

            {/* Trees */}
            {treesData.map((t, i) => (
                <Tree
                    key={i}
                    position={t.position}
                    rotation={t.rotation}
                    scale={t.scale}
                    material={material}
                    nodes={nodes}
                />
            ))}

            {/* Ambient dimmer based on cabin hover */}
            <AmbientDimmer
                lightRef={ambientRef}
                baseIntensity={TERRAIN_CONFIG.ambientIntensity}
            />
        </group>
    )
}
